/* Farmacias LF · carro (cajón y página /cart).
   Complementa assets/cart.js y assets/cart-drawer.js sin reemplazarlos. Todo va por delegación en document
   porque el cajón y la página se re-renderizan por secciones al cambiar cantidades.

   1. Avisos de receta: ninguna condición de venta bloquea el pago. La receta médica (R), la retenida (RR) y la cheque
      (RCH) se pagan online; su bloque pide la receta por WhatsApp y, con RCH, avisa que todo el pedido se retira en
      sucursal. Los enlaces [data-lf-ir-bloqueo] llevan al aviso correspondiente.
   2. Factura (<lf-cart-factura data-lf-fact>): casilla «¿Necesitas factura?», formato y validación del RUT (dígito
      verificador, solo en el navegador: la razón social la escribe el cliente, no se consulta a ningún servicio) y
      guardado de atributos con fetch a routes.cart_update_url. Claves: tipo_documento (boleta | factura), rut,
      razon_social, giro, direccion. Al re-renderizarse el carro (cantidades), el bloque nuevo cede su lugar al que el
      cliente estaba llenando solo si ese tiene algo que el carro aún no muestra: no se pierde lo escrito, no quedan dos
      y lo que se corrigió en otra pestaña gana. En la página, la barra fija del celular la recuerda ([data-lf-fact-eco])
      hasta que la casilla se ve en la pantalla, y los dos «Ir a pagar» se frenan si faltan datos, llevando al que
      falta.
   3. Los cambios dentro de [data-lf-aislado] (factura, nota, buscador del carro vacío) no llegan a
      <cart-items>/<cart-drawer-items>, que tratarían cualquier «change» como un cambio de cantidad.
   4. Enter en una cantidad actualiza la cantidad en vez de enviar el formulario (que iría a pagar). */
(() => {
  if (window.LFCart) return;

  const CAMPOS = ['rut', 'razon_social', 'giro', 'direccion'];
  const MSG = {
    requerido: 'Completa este dato.',
    rutInvalido: 'Revisa el RUT: el dígito verificador no coincide.',
    rutOk: 'RUT válido.',
    guardarError: 'No pudimos guardar los datos de factura. Revisa tu conexión e inténtalo de nuevo.',
  };

  /* ---------- RUT ---------- */
  const limpiarRut = (v) => String(v || '').replace(/[^0-9kK]/g, '').toUpperCase();

  const formatearRut = (v) => {
    const r = limpiarRut(v);
    if (r.length < 2) return r;
    const dv = r.slice(-1);
    const cuerpo = r.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${cuerpo}-${dv}`;
  };

  const validarRut = (v) => {
    const r = limpiarRut(v);
    if (r.length < 8 || r.length > 9) return false;
    const cuerpo = r.slice(0, -1);
    if (!/^\d+$/.test(cuerpo)) return false;
    let suma = 0;
    let m = 2;
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += Number(cuerpo[i]) * m;
      m = m < 7 ? m + 1 : 2;
    }
    const resto = 11 - (suma % 11);
    const esperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
    return r.slice(-1) === esperado;
  };

  /* ---------- Utilidades ---------- */
  const updateUrl = () => (window.routes && window.routes.cart_update_url) || '/cart/update';
  const jsonConfig = () =>
    typeof window.fetchConfig === 'function'
      ? window.fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  const campo = (fact, key) => fact.querySelector(`[data-lf-fact-campo="${key}"]`);
  // aria-describedby puede nombrar también la ayuda del campo: el mensaje es el .lf-fact__msg
  const msgDe = (input) =>
    String((input && input.getAttribute('aria-describedby')) || '')
      .split(/\s+/)
      .map((id) => (id ? document.getElementById(id) : null))
      .find((el) => el && el.classList.contains('lf-fact__msg')) || null;

  const setMsg = (input, texto, tipo) => {
    const el = msgDe(input);
    if (el) {
      el.textContent = texto || '';
      el.classList.toggle('lf-fact__msg--error', tipo === 'error');
      el.classList.toggle('lf-fact__msg--ok', tipo === 'ok');
    }
    if (tipo === 'error') {
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('lf-input--error');
    } else {
      input.removeAttribute('aria-invalid');
      input.classList.remove('lf-input--error');
    }
  };

  const activa = (fact) => {
    const cb = fact.querySelector('[data-lf-fact-cb]');
    return !!(cb && cb.checked);
  };

  /* Los atributos que el bloque manda al carro. Con inicial, los que pintó el servidor (defaultValue / defaultChecked:
     no cambian con lo que se escribe ni con lo que el navegador restaura al volver con Atrás). */
  const datosDe = (fact, inicial) => {
    const cb = fact.querySelector('[data-lf-fact-cb]');
    const on = !!(cb && (inicial ? cb.defaultChecked : cb.checked));
    const d = { tipo_documento: on ? 'factura' : 'boleta' };
    CAMPOS.forEach((k) => {
      const i = campo(fact, k);
      d[k] = on && i ? (inicial ? i.defaultValue : i.value).trim() : '';
    });
    return d;
  };
  const clave = (d) => JSON.stringify(d);

  /* ---------- Guardado de atributos ---------- */
  const timers = new WeakMap();
  /* Por bloque: pendiente = hay algo escrito que aún no se manda; enviando = guardados sin respuesta; superados = lo que
     el servidor pudo mostrar antes de lo último que este bloque mandó (lo que pintó al nacer y cada envío). */
  const estados = new WeakMap();
  const estadoDe = (fact) => {
    let st = estados.get(fact);
    if (!st) estados.set(fact, (st = { pendiente: false, enviando: 0, superados: null }));
    return st;
  };

  const guardar = (fact) => {
    const attributes = datosDe(fact);
    const st = estadoDe(fact);
    if (!st.superados) st.superados = new Set([clave(datosDe(fact, true))]);
    st.superados.add(clave(attributes));
    st.pendiente = false;
    st.enviando += 1;
    // keepalive: el guardado termina aunque el cliente se vaya de la página (Agregar, Ir a pagar)
    return fetch(updateUrl(), { ...jsonConfig(), keepalive: true, body: JSON.stringify({ attributes }) })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
      })
      .catch(() => {
        st.pendiente = true;
        const rut = campo(fact, 'rut');
        if (rut) setMsg(rut, MSG.guardarError, 'error');
      })
      .finally(() => {
        st.enviando -= 1;
      });
  };

  const guardarLuego = (fact, ms = 700) => {
    clearTimeout(timers.get(fact));
    timers.set(
      fact,
      setTimeout(() => guardar(fact), ms)
    );
  };

  const guardarYa = (fact) => {
    clearTimeout(timers.get(fact));
    return guardar(fact);
  };

  /* ---------- Factura: abrir / cerrar ---------- */
  const toggleFactura = (fact) => {
    const on = activa(fact);
    const cb = fact.querySelector('[data-lf-fact-cb]');
    const tipo = fact.querySelector('[data-lf-fact-tipo]');
    const box = cb && document.getElementById(cb.getAttribute('aria-controls'));
    if (tipo) tipo.value = on ? 'factura' : 'boleta';
    if (cb) cb.setAttribute('aria-expanded', String(on));
    if (box) box.hidden = !on;
    CAMPOS.forEach((k) => {
      const i = campo(fact, k);
      if (!i) return;
      i.disabled = !on;
      if (!on) setMsg(i, '', null);
    });
    guardarYa(fact);
    // quien la marcó o la desmarcó ya la vio: al desmarcar, la barra no vuelve a pedirla
    if (fact.dataset.lfFact) vistas[fact.dataset.lfFact] = true;
    marcarEco(fact);
    if (on) {
      const rut = campo(fact, 'rut');
      if (rut) setTimeout(() => rut.focus({ preventScroll: false }), 40);
    }
  };

  /* ---------- Factura: validación ---------- */
  const validarCampo = (input) => {
    const key = input.dataset.lfFactCampo;
    const v = input.value.trim();
    if (!v) {
      setMsg(input, MSG.requerido, 'error');
      return false;
    }
    if (key === 'rut' && !validarRut(v)) {
      setMsg(input, MSG.rutInvalido, 'error');
      return false;
    }
    if (key === 'rut') setMsg(input, MSG.rutOk, 'ok');
    else setMsg(input, '', null);
    return true;
  };

  /* Devuelve el primer campo con error, o null si todo está bien (o si no se pidió factura). */
  const validarFactura = (fact) => {
    if (!fact || !activa(fact)) return null;
    let primero = null;
    CAMPOS.forEach((k) => {
      const i = campo(fact, k);
      if (i && !validarCampo(i) && !primero) primero = i;
    });
    return primero;
  };

  /* Sin marcar errores: ¿están todos los datos (y el RUT con su dígito verificador)? */
  const datosCompletos = (fact) =>
    CAMPOS.every((k) => {
      const i = campo(fact, k);
      const v = i ? i.value.trim() : '';
      return !!v && (k !== 'rut' || validarRut(v));
    });

  const irACampo = (input) => {
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  };

  /* ---------- Barra fija de la página del carro (celular) ---------- */
  /* «¿Necesitas factura? Pídela aquí» mientras no se pida y la casilla no se haya visto en la pantalla (vistas);
     «Factura: completa los datos…» si se pidió y faltan, aunque ya se haya visto; oculta con todo completo. */
  const vistas = {};
  const ojos = {};
  const marcarEco = (fact) => {
    const pre = fact && fact.dataset.lfFact;
    const eco = pre && document.querySelector(`[data-lf-fact-eco="${pre}"]`);
    if (!eco) return;
    const tx = eco.querySelector('[data-lf-fact-eco-tx]');
    if (tx && !eco.dataset.txNo) eco.dataset.txNo = tx.textContent;
    const on = activa(fact);
    const falta = on && !datosCompletos(fact);
    eco.hidden = on ? !falta : !!vistas[pre];
    eco.classList.toggle('is-falta', falta);
    if (tx) tx.textContent = falta ? eco.dataset.txFalta || eco.dataset.txNo : eco.dataset.txNo;
  };

  /* La casilla se vio: entró entera en la pantalla, sobre la barra fija. Desde ahí la línea «Pídela aquí» no vuelve en
     esa visita (como la de la comuna, que se va al elegir una), así la barra no queda más alta para todos. */
  const observar = (fact) => {
    const pre = fact.dataset.lfFact;
    const eco = pre && document.querySelector(`[data-lf-fact-eco="${pre}"]`);
    const tog = fact.querySelector('.lf-fact__toggle');
    if (!eco || !tog || vistas[pre] || !('IntersectionObserver' in window)) return;
    const bar = eco.closest('.lf-cpaybar');
    const alto = bar && getComputedStyle(bar).position === 'fixed' ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    if (ojos[pre]) ojos[pre].disconnect();
    const io = new IntersectionObserver(
      (entradas) => {
        if (!entradas.some((x) => x.isIntersecting && x.intersectionRatio >= 0.99)) return;
        io.disconnect();
        vistas[pre] = true;
        if (vivos[pre]) marcarEco(vivos[pre]);
      },
      { rootMargin: `0px 0px -${alto}px 0px`, threshold: 1 }
    );
    ojos[pre] = io;
    io.observe(tog);
  };

  /* ---------- <lf-cart-factura>: conserva lo escrito al re-renderizarse el carro ---------- */
  /* cart.js y cart-drawer.js reemplazan el HTML del resumen o del cajón al cambiar cantidades (y el cajón al agregar).
     El bloque recién llegado del servidor (nuevo) cede su lugar al que estaba en la página (vivo, mismo prefijo, ya
     fuera) solo si el vivo tiene algo que el carro aún no muestra; si no, manda el servidor. En la página queda uno
     solo. En el editor del tema no se cambia (ahí se re-renderiza para ver los textos nuevos).
     Devuelve false (queda el nuevo), true (queda el vivo) o 'reafirmar' (queda el vivo y vuelve a guardar). */
  const conservar = (vivo, nuevo) => {
    const st = estadoDe(vivo);
    const llega = clave(datosDe(nuevo));
    // Mismos datos: queda el vivo, con sus mensajes («RUT válido.», «Completa este dato.»)
    if (llega === clave(datosDe(vivo))) return true;
    // Lo que se está escribiendo aún no llega al carro (guardado por mandar, en camino o fallido)
    if (st.pendiente || st.enviando > 0) return true;
    // El servidor pintó un estado que este bloque ya dejó atrás (el re-render salió antes de aplicarse el último
    // guardado): queda lo escrito y se vuelve a mandar por si ese guardado se perdió
    if (st.superados && st.superados.has(llega)) return 'reafirmar';
    // El carro cambió por otro lado (otra pestaña, o lo que el navegador restauró al volver con Atrás): manda el servidor
    return false;
  };

  const vivos = {};
  if (window.customElements && !window.customElements.get('lf-cart-factura')) {
    window.customElements.define(
      'lf-cart-factura',
      class extends HTMLElement {
        connectedCallback() {
          const pre = this.dataset.lfFact || '';
          const vivo = vivos[pre];
          const editor = !!(window.Shopify && window.Shopify.designMode);
          if (vivo && vivo !== this && !vivo.isConnected && !editor) {
            const queda = conservar(vivo, this);
            if (queda) {
              this.replaceWith(vivo);
              if (queda === 'reafirmar') guardarYa(vivo);
              return;
            }
            clearTimeout(timers.get(vivo));
          }
          vivos[pre] = this;
          marcarEco(this);
          observar(this);
        }
      }
    );
  }

  /* ---------- Eventos ---------- */
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest('[data-lf-aislado]')) return;
      // Que <cart-items>/<cart-drawer-items> no lo traten como un cambio de cantidad.
      e.stopPropagation();

      const fact = t.closest('[data-lf-fact]');
      if (!fact) return;
      if (t.matches('[data-lf-fact-cb]')) {
        estadoDe(fact).pendiente = true;
        toggleFactura(fact);
        return;
      }
      if (!t.matches('[data-lf-fact-campo]')) return;
      estadoDe(fact).pendiente = true;
      if (t.dataset.lfFactCampo === 'rut') {
        t.value = formatearRut(t.value);
        validarCampo(t);
      } else if (t.value.trim()) {
        setMsg(t, '', null);
      }
      guardarYa(fact);
      marcarEco(fact);
    },
    true
  );

  document.addEventListener('input', (e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.matches('[data-lf-fact] [data-lf-fact-campo]')) return;
    const fact = t.closest('[data-lf-fact]');
    estadoDe(fact).pendiente = true;
    if (t.dataset.lfFactCampo === 'rut') {
      // Formato en vivo solo si se escribe al final, para no mover el cursor.
      const alFinal = t.selectionStart === t.value.length;
      if (alFinal) t.value = formatearRut(t.value);
      if (t.getAttribute('aria-invalid') === 'true' && validarRut(t.value)) setMsg(t, MSG.rutOk, 'ok');
    }
    guardarLuego(fact);
    marcarEco(fact);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (t.matches('[data-lf-fact] [data-lf-fact-campo]')) {
      e.preventDefault();
      const fact = t.closest('[data-lf-fact]');
      const lista = CAMPOS.map((k) => campo(fact, k)).filter(Boolean);
      const sig = lista[lista.indexOf(t) + 1];
      if (sig) sig.focus();
      else t.blur();
      return;
    }

    if (t.matches('.lf-cqty__in')) {
      e.preventDefault();
      t.blur();
    }
  });

  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('[data-lf-ir-bloqueo]') : null;
    if (!link) return;
    // «Factura: completa los datos…» de la barra fija lleva al dato que falta (si no, a la casilla, como los demás)
    if (link.matches('[data-lf-fact-eco]')) {
      const fact = document.querySelector(`[data-lf-fact="${link.dataset.lfFactEco}"]`);
      const malo = validarFactura(fact);
      if (malo) {
        e.preventDefault();
        irACampo(malo);
        return;
      }
    }
    const id = (link.getAttribute('href') || '').replace('#', '');
    const destino = id && document.getElementById(id);
    if (!destino) return;
    e.preventDefault();
    destino.scrollIntoView({ behavior: 'smooth', block: 'start' });
    destino.focus({ preventScroll: true });
  });

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
      if (e.submitter && e.submitter.name !== 'checkout') return;

      const tipo = form.elements.namedItem('attributes[tipo_documento]');
      const fact = tipo && tipo.closest ? tipo.closest('[data-lf-fact]') : null;
      const malo = validarFactura(fact);
      if (malo) {
        e.preventDefault();
        e.stopImmediatePropagation();
        marcarEco(fact);
        irACampo(malo);
      }
    },
    true
  );

  window.LFCart = { limpiarRut, formatearRut, validarRut, validarFactura };
})();
